'use client'
import FiltersSection from '@/components/GlobalFilter'
import SearchIcon from '@/components/icon-holder/SearchIcon'
import SectionContainer from '@/components/SectionContainer'
import SortDropdown from '@/components/SortDropdown'
import { OPEN_LISTING_SEARCH_DATES_EVENT } from '@/components/searchBox/events'
import { Button, Drawer } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { analytics } from '@repo/analytics'
import { trackMetaEvent } from '@repo/ui'
import { useTranslations } from 'next-intl'
import { useSearchParams } from 'next/navigation'
import { useEffect, useMemo, useRef, useState } from 'react'
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md'
import { RiInformationLine, RiEqualizerLine } from 'react-icons/ri'
import Skeleton from 'react-loading-skeleton'
import Navbar from '../dashboard/newUi/Navbar'
import PropertyCard from '../dashboard/ui/PropertyCard'
import { usePropertyListing } from './usePropertyListing'

// Mirrors PropertyCard's non-home grid layout element for element, so the
// swap from skeleton to card causes no reflow. Keep in sync with PropertyCard
// and PropertyCardParts.
const PropertyCardSkeleton = () => (
  <div className='w-full'>
    {/* PropertyImageSection */}
    <div className='rounded-t-[20px] w-full aspect-square overflow-hidden bg-gray-200'>
      <Skeleton height='100%' />
    </div>
    <div className='border border-[#EDEDED] rounded-b-[20px] p-4 bg-white'>
      <div className='flex flex-col gap-2'>
        {/* Name — line-clamp-2 in a fixed h-[48px] box */}
        <div className='h-[48px] flex flex-col gap-2'>
          <Skeleton height={16} width='90%' />
          <Skeleton height={16} width='65%' />
        </div>
        {/* PropertyLocation + ListingPropertyReviews pill */}
        <div className='flex items-center justify-between gap-2'>
          <div className='flex items-center gap-1'>
            <Skeleton width={16} height={16} />
            <Skeleton width={72} height={12} />
          </div>
          <Skeleton width={127} height={22} borderRadius={21} />
        </div>
        {/* PropertyUnitFacts — icon + label per fact */}
        <div className='flex items-center gap-3 h-[18px]'>
          <div className='flex items-center gap-1'>
            <Skeleton width={14} height={14} />
            <Skeleton width={58} height={12} />
          </div>
          <div className='flex items-center gap-1'>
            <Skeleton width={14} height={14} />
            <Skeleton width={42} height={12} />
          </div>
          <div className='flex items-center gap-1'>
            <Skeleton width={14} height={14} />
            <Skeleton width={48} height={12} />
          </div>
        </div>
        {/* NonHomePriceSection — single row, discount pill pushed right */}
        <div className='pt-2.5 flex items-center gap-1.5'>
          <Skeleton width={72} height={18} />
          <Skeleton width={44} height={12} />
          <Skeleton width={38} height={11} />
          <Skeleton width={13} height={13} circle />
          <div className='ml-auto'>
            <Skeleton width={62} height={24} borderRadius={30} />
          </div>
        </div>
      </div>
    </div>
  </div>
)

// Build a compact page list with ellipses, e.g. 1 … 4 [5] 6 … 20.
// First and last pages are always shown; the current page is always centred.
function getPaginationItems(
  current: number,
  total: number,
): Array<number | 'ellipsis'> {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }
  const items: Array<number | 'ellipsis'> = [1]
  const left = Math.max(2, current - 1)
  const right = Math.min(total - 1, current + 1)
  if (left > 2) items.push('ellipsis')
  for (let page = left; page <= right; page += 1) items.push(page)
  if (right < total - 1) items.push('ellipsis')
  items.push(total)
  return items
}

export default function PropertyListing() {
  const t = useTranslations('propertyListing')
  const searchParams = useSearchParams()
  const searchParamsString = searchParams.toString()
  const location = searchParams.get('location')
  const checkin = searchParams.get('checkin')
  const checkout = searchParams.get('checkout')
  const guests = searchParams.get('guests')
  const flex_days = searchParams.get('flex_days')

  const adults = searchParams.get('adults')
  const children = searchParams.get('children')
  const infants = searchParams.get('infants')

  const {
    ITEMS_PER_PAGE,
    filteredData,
    isLoading,
    isLoadingMore,
    hasNext,
    hasPrevious,
    totalProperties,
    totalPages,
    currentPage,
    startItem,
    endItem,
    searchProperties,
    setSearchProperties,
    sortDataSet,
    setSortDataSet,
    selectedBedroom,
    setSelectedBedroom,
    selectedPropertyType,
    setSelectedPropertyType,
    priceRange,
    minMaxPrice,
    setPriceRange,
    handleNextPage,
    handlePreviousPage,
    goToPage,
    resetPriceFilter, // Add this
  } = usePropertyListing({
    location,
    checkin,
    checkout,
    guests,
    adults,
    children,
    infants,
    flex_days,
  })

  const view = 'grid' as const
  const [opened, setOpened] = useState(false)
  const isMobile = useMediaQuery('(max-width: 767px)')
  const trackedSearchKeys = useRef<Set<string>>(new Set())
  const impressedProperties = useRef<Set<string>>(new Set())

  // Carry the active search (dates/guests/location) onto the property links so
  // the detail page can pre-fill them — but only include params that actually
  // have a value. Emitting empty `?checkin=&guests=&…` just clutters the URL and
  // can mislead logic that checks for the param's presence (e.g. the detail
  // page breadcrumb treats an empty `location=` as "came from a search").
  const detailQuery = useMemo(() => {
    const params = new URLSearchParams()
    const entries: Record<string, string | null> = {
      location,
      checkin,
      checkout,
      guests,
      adults,
      children,
      infants,
    }
    for (const [key, value] of Object.entries(entries)) {
      if (value) params.set(key, value)
    }
    const qs = params.toString()
    return qs ? `?${qs}` : ''
  }, [location, checkin, checkout, guests, adults, children, infants])

  const metaSearchParams = useMemo(() => {
    const params: Record<string, string | number> = {
      content_category: 'Stays Property Listing',
      content_type: 'product_group',
      search_string: location || 'All destinations',
      num_items: Number(totalProperties) || 0,
    }

    if (location) params.location = location
    if (checkin) params.checkin_date = checkin
    if (checkout) params.checkout_date = checkout
    if (flex_days) params.flex_days = flex_days

    const addNumericParam = (key: string, value: string | null) => {
      if (!value) return
      const numericValue = Number(value)
      if (Number.isFinite(numericValue)) params[key] = numericValue
    }

    addNumericParam('guests', guests)
    addNumericParam('adults', adults)
    addNumericParam('children', children)
    addNumericParam('infants', infants)

    return params
  }, [location, checkin, checkout, flex_days, guests, adults, children, infants, totalProperties])

  // Count active filters for badge
  const activeFilterCount = useMemo(() => {
    let count = 0
    if (searchProperties) count++
    if (selectedBedroom !== 'All') count++
    if (selectedPropertyType) count++
    if (priceRange[0] !== minMaxPrice[0] || priceRange[1] !== minMaxPrice[1]) count++
    return count
  }, [searchProperties, selectedBedroom, selectedPropertyType, priceRange, minMaxPrice])

  // Calculate if it's a short stay (3 nights or less)
  const isShortStay = useMemo(() => {
    if (!checkin || !checkout) return false

    try {
      // Attempt to parse the date strings. If they are invalid, return false.
      const checkInDate = new Date(checkin)
      const checkOutDate = new Date(checkout)

      // Check if the dates are valid
      if (isNaN(checkInDate.getTime()) || isNaN(checkOutDate.getTime())) {
        return false
      }

      const nights = Math.ceil(
        (checkOutDate.getTime() - checkInDate.getTime()) /
          (1000 * 60 * 60 * 24),
      )
      return nights <= 3 && nights > 0
    } catch (error) {
      // Handle any potential errors during date parsing
      console.error('Error parsing dates:', error)
      return false
    }
  }, [checkin, checkout])

  const handleExtendStay = () => {
    window.dispatchEvent(new CustomEvent(OPEN_LISTING_SEARCH_DATES_EVENT))
  }

  useEffect(() => {
    if (!isMobile && opened) {
      setOpened(false) // Close drawer when resizing to desktop
    }
  }, [isMobile, opened])

  useEffect(() => {
    if (isLoading) return

    const trackingKey = searchParamsString || 'property-listing'
    if (trackedSearchKeys.current.has(trackingKey)) return

    trackedSearchKeys.current.add(trackingKey)
    trackMetaEvent('Search', metaSearchParams)
  }, [isLoading, metaSearchParams, searchParamsString])

  // Fire a deduped `listing_impression` for each property shown in the results
  // (the top-of-funnel "results seen" step). Deduped per property so re-renders
  // and filter changes don't re-count the same listing.
  useEffect(() => {
    if (isLoading || !filteredData?.length) return
    filteredData.forEach((property, index) => {
      const item = property as { property_uid?: string; id?: string | number }
      const id = item.property_uid ?? item.id
      if (id == null) return
      const key = String(id)
      if (impressedProperties.current.has(key)) return
      impressedProperties.current.add(key)
      analytics.listingImpression(key, index, 'property_listing')
    })
  }, [filteredData, isLoading])

  return (
    <section className='scroll-smooth max-w-[1440px] mx-auto'>
      <Navbar
        loading={false}
        listShowing={true}
        headphone={true}
      />
      <SectionContainer className='mb-8 md:max-w-6xl lg:max-w-7xl max-md:max-w-full mx-auto px-3'>
<div className='flex md:hidden gap-3 mb-[30px]'>
          <Button
            type='button'
            radius='xl'
            variant='outline'
            className='!h-[40px] !border-[#E0E0E0] !text-[#191919] font-medium !text-[13px] flex-1'
            onClick={() => setOpened(true)}
          >
            <div className='flex items-center gap-2'>
              <RiEqualizerLine size={16} />
              <span>{t('actions.filters')}</span>
              {activeFilterCount > 0 && (
                <span className='bg-[#80c7e2] text-white text-[10px] font-semibold rounded-full w-[18px] h-[18px] flex items-center justify-center'>
                  {activeFilterCount}
                </span>
              )}
            </div>
          </Button>
          <SortDropdown
            onSortChange={setSortDataSet}
            currentSort={sortDataSet}
            isMobileVisible
          />
          <Drawer
            position='bottom'
            size='xl'
            opened={opened}
            onClose={() => setOpened(false)}
            radius={0}
            styles={{
              content: {
                borderRadius: '42px 42px 0 0',
                backgroundColor: '#FFFFFF',
              },
            }}
          >
            <div className='px-2 pb-24 w-full'>
              <FiltersSection
                loading={isLoading}
                data={filteredData}
                searchProperties={searchProperties}
                setSearchProperties={setSearchProperties}
                selectedBedroom={selectedBedroom}
                setSelectedBedroom={setSelectedBedroom}
                priceRange={priceRange}
                setPriceRange={setPriceRange}
                minMaxPrice={minMaxPrice}
                selectedPropertyType={selectedPropertyType}
                setSelectedPropertyType={setSelectedPropertyType}
                resetPriceFilter={resetPriceFilter}
              />
            </div>
            {/* Sticky bottom bar */}
            <div className='fixed bottom-0 left-0 right-0 bg-white border-t border-[#E0E0E0] px-4 py-3 z-[300]'>
              <Button
                type='button'
                fullWidth
                radius='xl'
                variant='filled'
                className='!h-[48px] !bg-[#80c7e2] hover:!bg-[#6ab8d6] !text-white font-semibold !text-[14px]'
                onClick={() => setOpened(false)}
              >
                {totalProperties !== undefined
                  ? t('actions.showProperties', {
                    count: Number(totalProperties),
                  })
                  : t('actions.showResults')}
              </Button>
            </div>
          </Drawer>
        </div>

        <div className='flex gap-8 items-start'>
          <div className='hidden md:block sticky top-4 h-fit flex-shrink-0'>
            <FiltersSection
              loading={isLoading}
              data={filteredData}
              searchProperties={searchProperties}
              setSearchProperties={setSearchProperties}
              selectedBedroom={selectedBedroom}
              setSelectedBedroom={setSelectedBedroom}
              priceRange={priceRange}
              setPriceRange={setPriceRange}
              minMaxPrice={minMaxPrice}
              selectedPropertyType={selectedPropertyType}
              setSelectedPropertyType={setSelectedPropertyType}
              resetPriceFilter={resetPriceFilter} // Add this
            />
          </div>

          <div className='flex-1 min-w-0'>
            <div className='hidden md:flex items-center justify-between pb-4'>
              <span className='text-[#6A6B71] text-sm'>
                {location || 'All destinations'} :{' '}
                <span className='text-[#6A6B71] text-[14px]'>
                  {totalProperties ?? 0}{' '}
                  {Number(totalProperties) === 1 ? 'property' : 'properties'}{' '}
                  found
                </span>
              </span>
              <SortDropdown
                onSortChange={setSortDataSet}
                currentSort={sortDataSet}
              />
            </div>
            <div className='md:hidden text-[#6A6B71] text-sm pb-4'>
              {location || 'All destinations'} :{' '}
              <span className='text-[#6A6B71] text-[14px]'>
                {totalProperties ?? 0}{' '}
                {Number(totalProperties) === 1 ? 'property' : 'properties'}{' '}
                found
              </span>
            </div>
            {/* Short Stay Tip Banner - Only show if there are properties */}
            {isShortStay && filteredData.length > 0 && (
              <div className='w-full mb-6 md:mb-8'>
                <div className='bg-[#FFF9E2] rounded-xl p-3 md:p-4 flex items-start gap-2 md:gap-3'>
                  <RiInformationLine className='w-4 h-4 md:w-5 md:h-5 text-[#191919] flex-shrink-0' />
                  <div className='flex-1 min-w-0'>
                    <p className='text-[#191919] text-xs md:text-sm font-normal leading-[18px] md:leading-5'>
                      <span className='font-semibold'>Tip:</span> You may be
                      able to see more properties if you extend your stay
                      duration.
                    </p>
                  </div>
                </div>
              </div>
            )}
            {view === 'grid' ? (
              <>
                {isLoading ? (
                  <div className='gap-8 md:!gap-y-[35px] md:gap-x-[22px] grid grid-cols-1 md:grid-cols-2 min-[70.0625rem]:grid-cols-3'>
                    {Array.from({ length: ITEMS_PER_PAGE }).map((_, i) => (
                      <PropertyCardSkeleton key={i} />
                    ))}
                  </div>
                ) : filteredData.length > 0 ? (
                  <div className='gap-8 md:!gap-y-[35px] md:gap-x-[22px] grid grid-cols-1 md:grid-cols-2 min-[70.0625rem]:grid-cols-3'>
                    {filteredData.map((property, index) => (
                      <PropertyCard
                        key={property.id || index}
                        className='w-full'
                        property={property}
                        queryPrams={detailQuery}
                        propertylisting={true}
                        city={property.city}
                        view={view}
                      />
                    ))}
                  </div>
                ) : (
                  <>
                    {isShortStay ? (
                      <div className='flex flex-col w-[423px] items-center justify-self-center justify-center py-8 md:py-12'>
                        <SearchIcon className='w-10 h-10 md:w-[51px] md:h-[50px] text-[#010101] mb-4 md:mb-6' />
                        <p className='text-[#191919] text-[14px] font-[500] mb-2 text-center'>
                          {t('emptyState.shortStayTitle')}
                        </p>
                        <p className='text-[#6A6B71] px-3 text-[14px] font-[500] mb-4 text-center max-w-md'>
                          {t('emptyState.shortStayDescription')}
                        </p>
                        <button
                          type='button'
                          onClick={handleExtendStay}
                          aria-label={t('emptyState.extendStayAriaLabel')}
                          className='px-[18px] py-[8px] cursor-pointer border border-[#191919] text-[#191919] hover:bg-[#191919] hover:text-white text-[13px] font-semibold rounded-lg transition-colors duration-200'
                        >
                          {t('emptyState.extendStay')}
                        </button>
                      </div>
                    ) : (
                      <p className='text-sm font-semibold text-center mt-5'>
                        {t('emptyState.noMatches')}
                      </p>
                    )}
                  </>
                )}
                {/* Pagination Controls */}
                {totalProperties > 0 && (
                  <nav
                    aria-label='Pagination'
                    className='mt-10 flex flex-col-reverse items-center gap-4 sm:flex-row sm:justify-between'
                  >
                    <p className="text-[#6A6B71] text-xs font-normal font-['Poppins'] leading-tight">
                      {t('pagination.summary', {
                        start: startItem,
                        end: endItem,
                        total: Number(totalProperties),
                      })}
                    </p>
                    {totalPages > 1 && (
                      <div className='flex items-center gap-1.5'>
                        <button
                          type='button'
                          onClick={handlePreviousPage}
                          disabled={!hasPrevious || isLoadingMore || isLoading}
                          aria-label={t('pagination.previousPage')}
                          className='flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-[#E6E6E6] text-[#191919] transition-colors hover:bg-[#191919] hover:text-white hover:border-[#191919] disabled:cursor-not-allowed disabled:opacity-40'
                        >
                          <MdKeyboardArrowLeft className='h-5 w-5' />
                        </button>

                        {getPaginationItems(currentPage, totalPages).map((item, index) =>
                          item === 'ellipsis' ? (
                            <span
                              key={`ellipsis-${index}`}
                              className='flex h-9 w-9 select-none items-center justify-center text-[#9A9A9A]'
                            >
                              …
                            </span>
                          ) : (
                            <button
                              key={item}
                              type='button'
                              onClick={() => goToPage(item)}
                              disabled={isLoadingMore || isLoading}
                              aria-current={item === currentPage ? 'page' : undefined}
                              className={`flex h-9 min-w-9 cursor-pointer items-center justify-center rounded-lg px-3 text-sm font-medium transition-colors disabled:cursor-not-allowed ${
                                item === currentPage
                                  ? 'bg-[#191919] text-white hover:opacity-75'
                                  : 'border border-[#E6E6E6] text-[#191919] hover:bg-[#191919] hover:text-white hover:border-[#191919]'
                              }`}
                            >
                              {item}
                            </button>
                          ),
                        )}

                        <button
                          type='button'
                          onClick={handleNextPage}
                          disabled={!hasNext || isLoadingMore || isLoading}
                          aria-label={t('pagination.nextPage')}
                          className='flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-[#E6E6E6] text-[#191919] transition-colors hover:bg-[#191919] hover:text-white hover:border-[#191919] disabled:cursor-not-allowed disabled:opacity-40'
                        >
                          <MdKeyboardArrowRight className='h-5 w-5' />
                        </button>
                      </div>
                    )}
                  </nav>
                )}

                {isLoadingMore && !isLoading && (
                  <div className='gap-8 md:!gap-y-[35px] md:gap-x-[12px] grid grid-cols-1 md:grid-cols-2 min-[70.0625rem]:grid-cols-3 mt-8'>
                    {Array.from({ length: ITEMS_PER_PAGE }).map((_, i) => (
                      <PropertyCardSkeleton key={`loading-${i}`} />
                    ))}
                  </div>
                )}
              </>
            ) : null}
          </div>
        </div>
      </SectionContainer>
    </section>
  )
}
